import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import logo from "../../assets/logo.png";
import logowhite from "../../assets/logowhite.png";
import useSetup from "../../hooks/useSetup";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";
import { getBillingOffers } from "../../api/client";

// Componentes separados por etapa
import EtapaEmpresa from "../../components/EtapaEmpresa";
import EtapaClientesNiveis from "../../components/EtapaClientesNiveis";
import EtapaClientesCadastro from "../../components/EtapaClientesCadastro";
// import EtapaClientesClassificacao from "../../components/EtapaClientesClassificacao";
import EtapaProdutos from "../../components/EtapaProdutos";
import EtapaPipeline from "../../components/EtapaPipeline";
import EtapaSolicitacoes from "../../components/EtapaSolicitacoes";
import EtapaCanais from "../../components/EtapaCanais";
import EtapaCargos from "../../components/EtapaCargos";
import EtapaUsuarios from "../../components/EtapaUsuarios";
import EtapaResumoFinal from "../../components/EtapaResumoFinal";

const SETUP_STEP_KEYS = [
  "company",
  "clientsLevels",
  "clientsImport",
  "products",
  "pipeline",
  "requests",
  "channels",
  "roles",
  "users",
  "summary",
];

const SetupPage = () => {
  // console.log("SetupPage")
  const theme = useTheme();
  const navigate = useNavigate(); // Hook para navegação
  const { t } = useTranslation("onboarding");

  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  useEffect(() => {
    let mounted = true;
    getBillingOffers({ autoGenerate: 1 })
      .then((res) => {
        if (!mounted) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        if (items.length > 0) {
          setBillingHint("Detectamos oportunidade de upgrade/add-on para este workspace.");
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const steps = useMemo(
    () => SETUP_STEP_KEYS.map((key) => t(`setup.steps.${key}`)),
    [t]
  );
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [billingHint, setBillingHint] = useState("");

  // EtapaEmpresa
  const [linkDigitado, setLinkDigitado] = useState("");
  const [company, setCompany] = useState({});

  // EtapaClientes - Níveis de Consciência
  const {
    saveStepData,
    generateAwarenessLevels,
    generatePipelineStages,
    generateRequestTypes,
    generateRoles,
    isLoading,
  } = useSetup();
  const [hasImportedAwarenessLevels, setHasImportedAwarenessLevels] = useState(false);
  const [awarenessLevels, setAwarenessLevels] = useState(
    []
    // [
    //   { title: "Desconhece o Problema", description: "Ainda não sabe que tem um problema." },
    //   { title: "Consciente do Problema", description: "Sabe que tem um problema, mas não conhece soluções." },
    //   { title: "Consciente da Solução", description: "Conhece as soluções, mas não seu produto." },
    //   { title: "Consciente do Produto", description: "Conhece seu produto, mas não tem certeza se quer comprar." },
    //   { title: "Mais Consciente", description: "Está pronto para comprar seu produto." }
    // ]
  );
  const [selectedLevels, setSelectedLevels] = useState([true, true, true, false, false]);

  // EtapaClientes - Cadastro/Importação
  const [manualClients, setManualClients] = useState([]);
  const [newClient, setNewClient] = useState({ nome: "", email: "", telefone: "" });
  const [showManualForm, setShowManualForm] = useState(false);
  const [hasImportedClients, setHasImportedClients] = useState(false);

  // EtapaClientes - Classificação: dados placeholder para uso futuro (ex.: classificação por nível)
  // classifiedClients, awarenessColors, awarenessColorsDark podem ser usados em EtapaClientesClassificacao

  // EtapaProdutos
  const [produtos, setProdutos] = useState([]);
  const [showManualProductForm, setShowManualProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ nome: "", categoria: "", preco: "" });
  const [classifiedProducts] = useState([]);

  // EtapaPipeline
  const [pipelineStages, setPipelineStages] = useState([]); // Inicializado como array vazio
  const [newStage, setNewStage] = useState({ nome: "", ordem: 1 });

  // EtapaSolicitacoes
  const [requestTypes, setRequestTypes] = useState([]);
  const [requestTypesError, setRequestTypesError] = useState("");

  // EtapaPesquisas
  const [pesquisasConfiguradas, setPesquisasConfiguradas] = useState([]);
  
  // EtapaCargos
  const [cargosConfigurados, setCargosConfigurados] = useState([]);
  const [cargosError, setCargosError] = useState("");

  // EtapaUsuarios
  const [manualUsers, setManualUsers] = useState([]);
  const [newUser, setNewUser] = useState({ nome: "", email: "", telefone: "" });
  const [showManualFormUser, setShowManualFormUser] = useState(false);
  const [hasImportedUsers, setHasImportedUsers] = useState(false);

  //EtapaAutomacoes
  const suggestedRules = [
    {
      evento: "Novo Cliente Adicionado",
      condicao: "O cliente preencheu o formulário de cadastro",
      acao: "Enviar e-mail de boas-vindas",
    },
    {
      evento: "Compra Realizada",
      condicao: "O valor da compra é superior a R$ 500",
      acao: "Enviar cupom de desconto para próxima compra",
    },
    {
      evento: "Carrinho Abandonado",
      condicao: "O cliente não completou a compra dentro de 24 horas",
      acao: "Enviar lembrete por e-mail",
    },
  ];
  
  const eventosDisponiveis = [
    "Novo Cliente Adicionado",
    "Compra Realizada",
    "Carrinho Abandonado",
    "Visita ao Site",
    "Página de Produto Visualizada",
  ];
  
  const condicoesDisponiveis = [
    "O cliente preencheu o formulário de cadastro",
    "O valor da compra é superior a R$ 500",
    "O cliente não completou a compra dentro de 24 horas",
    "O cliente visitou a página de produto",
  ];
  
  const acoesDisponiveis = [
    "Enviar e-mail de boas-vindas",
    "Enviar cupom de desconto para próxima compra",
    "Enviar lembrete por e-mail",
    "Enviar e-mail com promoção do produto",
  ];  

  const [selectedSuggestedRules, setSelectedSuggestedRules] = useState([0, 2]);
  const [manualRules, setManualRules] = useState([
    {
      evento: "Visita ao Site",
      condicao: "O cliente visitou a página de produto",
      acao: "Enviar e-mail com promoção do produto",
    },
  ]);
  const [manualRule, setManualRule] = useState({
    evento: "",
    condicao: "",
    acao: "",
  });

  const handleToggleLevel = (index) => {
    const newSelected = [...selectedLevels];
    newSelected[index] = !newSelected[index];
    setSelectedLevels(newSelected);
  };

  const toggleAll = () => {
    const allSelected = selectedLevels.every(Boolean);
    setSelectedLevels(awarenessLevels.map(() => !allSelected));
  };

  const handleAddManualClient = () => {
    if (newClient.nome && newClient.email) {
      setManualClients((prev) => [...prev, newClient]);
      setNewClient({ nome: "", email: "", telefone: "" });
      setHasImportedClients(true);
    }
  };

  const handleAddManualUser = () => {
    if (newUser.nome && newUser.email) {
      setManualUsers((prev) => [...prev, newUser]);
      setNewUser({ nome: "", email: "", telefone: "" });
      setHasImportedUsers(true);
    }
  };

  const handleNext = async () => {
    setLoading(true);
    try {
      // Ao sair da etapa "Sobre a empresa", já grava a empresa no backend
      if (activeStep === 0 && company && company.companyName) {
        try {
          await saveStepData(1, {
            companyName: company.companyName,
            companyAbout: company.companyAbout,
            companyWebsite: company.companyWebsite,
            segmento: company.segmento,
            publicoAlvo: company.publicoAlvo,
            focoPrincipal: company.focoPrincipal,
            maturidadeDigital: company.maturidadeDigital,
            capacidadeEstrategica: company.capacidadeEstrategica,
          });
        } catch (e) {
          console.error("Falha ao salvar empresa durante o setup guiado:", e);
          // segue o fluxo mesmo assim, para não travar o usuário
        }
      }

      if (activeStep === steps.length - 1) {
        // Ao finalizar, marca o workspace como configurado e redireciona para /chat
        try {
          const activeCompany =
            sessionStorage.getItem("activeCompany") ||
            localStorage.getItem("company");
          if (activeCompany) {
            localStorage.setItem(
              `workspace-setup-done-${activeCompany}`,
              "1"
            );
          }
        } catch {
          // falha silenciosa; apenas não grava o flag
        }
        navigate("/chat");
      } else {
        setActiveStep((prev) => prev + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <EtapaEmpresa
            company={company}
            setCompany={setCompany}
            linkDigitado={linkDigitado}
            setLinkDigitado={setLinkDigitado}
          />
        );
      case 1:
        // console.log("renderStepContent switch")
          return (
              <EtapaClientesNiveis
              hasImportedAwarenessLevels={hasImportedAwarenessLevels}
              setHasImportedAwarenessLevels={setHasImportedAwarenessLevels}
              generateAwarenessLevels={generateAwarenessLevels}
              isLoading={isLoading}
              company={company}
              awarenessLevels={awarenessLevels}
              setAwarenessLevels={setAwarenessLevels}
              selectedLevels={selectedLevels}
              handleToggleLevel={handleToggleLevel}
              toggleAll={toggleAll}
              allSelected={selectedLevels.every(Boolean)}
              />
          );
      case 2:
            return (
                <EtapaClientesCadastro
                showManualForm={showManualForm}
                setShowManualForm={setShowManualForm}
                manualClients={manualClients}
                newClient={newClient}
                setNewClient={setNewClient}
                handleAddManualClient={handleAddManualClient}
                hasImportedClients={hasImportedClients}
                />
            );
      // case 3:
      //       return (
      //           <EtapaClientesClassificacao
      //           classifiedClients={classifiedClients}
      //           awarenessColors={awarenessColors}
      //           awarenessColorsDark={awarenessColorsDark}
      //           />
      //       );
      case 3:
        return (
          <EtapaProdutos
            showManualProductForm={showManualProductForm}
            setShowManualProductForm={setShowManualProductForm}
            manualProducts={produtos}
            setManualProducts={setProdutos}
            newProduct={newProduct}
            setNewProduct={setNewProduct}
            handleAddManualProduct={() => {
              if (newProduct.nome && newProduct.preco) {
                setProdutos((prev) => [...prev, newProduct]);
                setNewProduct({ nome: "", categoria: "", preco: "" });
              }
            }}
            classifiedProducts={classifiedProducts}
          />
        );
      case 4:
        return (
          <EtapaPipeline
            company={company}
            generatePipelineStages={generatePipelineStages}
            pipelineSteps={pipelineStages}
            setPipelineStages={setPipelineStages}
            newStage={newStage}
            setNewStage={setNewStage}
            handlePipelineChange={(index, field, value) => {
              const updatedSteps = [...pipelineStages];
              updatedSteps[index][field] = value;
              setPipelineStages(updatedSteps);
            }}
            handleAddStep={() => {
              const newStep = { from: "", to: "" };
              setPipelineStages((prevSteps) => [...prevSteps, newStep]);
            }}
            handleRemoveStep={(index) => {
              const updatedSteps = [...pipelineStages];
              updatedSteps.splice(index, 1);
              setPipelineStages(updatedSteps);
            }}
          />
        );
      case 5:
        return (
          <EtapaSolicitacoes
            requestTypes={requestTypes}
            isGenerating={isLoading}
            errorMessage={requestTypesError}
            onGenerateRequestTypes={async () => {
              setRequestTypesError("");
              try {
                const suggestedRaw = await generateRequestTypes(company);
                const suggested = Array.isArray(suggestedRaw)
                  ? suggestedRaw
                  : Array.isArray(suggestedRaw?.data)
                  ? suggestedRaw.data
                  : [];

                if (Array.isArray(suggested) && suggested.length) {
                  // Normaliza estrutura múltipla, mas força sempre os dois tipos fixos:
                  // "Atendimento" (relacionamento) e "Venda" (comercial).
                  const grupos = {
                    Atendimento: new Set(),
                    Venda: new Set(),
                  };

                  suggested.forEach((item) => {
                    const raiz = String(item.nome || item.tipo || item.type || "")
                      .toLowerCase();

                    // Decide a qual grupo pertence a linha
                    const isVenda =
                      raiz.includes("venda") ||
                      raiz.includes("comercial") ||
                      raiz.includes("oportunidade");
                    const grupo = isVenda ? "Venda" : "Atendimento";

                    const level1 = Array.isArray(item.subtipos) ? item.subtipos : [];
                    level1.forEach((s1) => {
                      if (s1?.nome) grupos[grupo].add(String(s1.nome).trim());
                      if (Array.isArray(s1.subtipos)) {
                        s1.subtipos.forEach((s2) => {
                          if (s2?.nome) grupos[grupo].add(String(s2.nome).trim());
                          if (Array.isArray(s2.subtipos)) {
                            s2.subtipos.forEach((s3) => {
                              if (s3?.nome) grupos[grupo].add(String(s3.nome).trim());
                            });
                          }
                        });
                      }
                    });
                  });

                  const normalized = [
                    {
                      tipo: "Atendimento",
                      subtipos:
                        grupos.Atendimento.size > 0
                          ? Array.from(grupos.Atendimento)
                          : [""],
                    },
                    {
                      tipo: "Venda",
                      subtipos:
                        grupos.Venda.size > 0
                          ? Array.from(grupos.Venda)
                          : [""],
                    },
                  ];

                  setRequestTypes(normalized);
                } else {
                  setRequestTypesError(
                    "Não encontramos sugestões automáticas neste momento. Você pode cadastrar os tipos manualmente."
                  );
                }
              } catch (e) {
                setRequestTypesError(
                  "Não foi possível gerar sugestões com IA agora. Verifique sua conexão ou tente novamente mais tarde. Você pode seguir cadastrando os tipos manualmente."
                );
              }
            }}
            handleTipoChange={(index, value) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes[index].tipo = value;
              setRequestTypes(newRequestTypes);
            }}
            handleSubtipoChange={(tipoIndex, subtipoIndex, value) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes[tipoIndex].subtipos[subtipoIndex] = value;
              setRequestTypes(newRequestTypes);
            }}
            handleAddTipo={() => {
              setRequestTypes([...requestTypes, { tipo: "", subtipos: [""] }]);
            }}
            handleRemoveTipo={(index) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes.splice(index, 1);
              setRequestTypes(newRequestTypes);
            }}
            handleAddSubtipo={(tipoIndex) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes[tipoIndex].subtipos.push("");
              setRequestTypes(newRequestTypes);
            }}
            handleRemoveSubtipo={(tipoIndex, subtipoIndex) => {
              const newRequestTypes = [...requestTypes];
              newRequestTypes[tipoIndex].subtipos.splice(subtipoIndex, 1);
              setRequestTypes(newRequestTypes);
            }}
          />
        );
      case 6:
        return <EtapaCanais canaisSelecionados={[]} setCanaisSelecionados={() => {}} />;
      case 7:
        return (
          <EtapaCargos
            roles={cargosConfigurados}
            onRolesChange={setCargosConfigurados}
            isGenerating={isLoading}
            errorMessage={cargosError}
            onGenerateRoles={async () => {
              setCargosError("");
              try {
                const suggestedRaw = await generateRoles(company);
                const suggested = Array.isArray(suggestedRaw)
                  ? suggestedRaw
                  : Array.isArray(suggestedRaw?.data)
                  ? suggestedRaw.data
                  : [];

                if (Array.isArray(suggested) && suggested.length) {
                  const normalized = suggested.map((item, idx) => ({
                    nome: item.cargo || item.nome || `Cargo ${idx + 1}`,
                    descricao: item.descricao || (typeof item === "string" ? item : ""),
                    ativo: true,
                  }));

                  setCargosConfigurados(normalized);
                } else {
                  setCargosError(
                    "Não encontramos sugestões automáticas de cargos neste momento. Você pode cadastrar os cargos manualmente."
                  );
                }
              } catch (e) {
                setCargosError(
                  "Não foi possível gerar sugestões de cargos com IA agora. Verifique sua conexão ou tente novamente mais tarde. Você pode seguir cadastrando os cargos manualmente."
                );
              }
            }}
          />
        );
      case 8:
        return (
          <EtapaUsuarios 
            showManualForm={showManualFormUser}
            setShowManualForm={setShowManualFormUser}
            manualUsers={manualUsers}
            newUser={newUser}
            setNewUser={setNewUser}
            handleAddManualUser={handleAddManualUser}
            hasImportedUsers={hasImportedUsers}
          />
        );
      case 9:
        return (
          <EtapaResumoFinal
            companyName={
              company.companyName ||
              company.nome ||
              company.name ||
              ""
            }
            selectedAwarenessTitles={
              Array.isArray(awarenessLevels) && awarenessLevels.length
                ? awarenessLevels
                    .map((level, idx) =>
                      selectedLevels[idx] && level?.title
                        ? level.title
                        : null
                    )
                    .filter(Boolean)
                : []
            }
            manualClients={manualClients}
            manualProducts={produtos}
            pipelineSteps={pipelineStages}
            requestTypes={requestTypes}
            canaisSelecionados={[]}
            pesquisasConfiguradas={pesquisasConfiguradas}
            cargosConfigurados={cargosConfigurados}
            usuariosCadastrados={manualUsers}
          />
        );
      default:
        return null;
    }
  };

  // Marca que o workspace já visitou o setup (mesmo que não tenha concluído)
  useEffect(() => {
    try {
      const activeCompany =
        sessionStorage.getItem("activeCompany") ||
        localStorage.getItem("company");
      if (activeCompany) {
        localStorage.setItem(
          `workspace-setup-visited-${activeCompany}`,
          "1"
        );
      }
    } catch {
      // falha silenciosa
    }
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
    //   justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{
        background: theme.palette.mode === "dark"
          ? "linear-gradient(135deg, #121212, #1e1e1e)"
          : "linear-gradient(135deg, #f5f5f5, #e3e3e3)",
        color: theme.palette.text.primary,
        padding: 2,
        overflow: "auto"
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="center" mb={4}>
        <img
          src={theme.palette.mode === "dark" ? logowhite : logo}
          alt={t("setup.logoAlt")}
          style={{ height: 50 }}
          loading="lazy"
        />
      </Box>
      <Paper
            elevation={6}
            sx={{
                padding: 4,
                width: "90%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                borderRadius: 3,
                bgcolor: theme.palette.background.paper,
            }}
            >
            <Typography variant="subtitle1" gutterBottom>
              {t("setup.subtitle")}
            </Typography>
            {billingHint ? (
              <Alert
                severity="warning"
                sx={{ width: "100%", mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => navigate("/settings/billing")}>
                    Ver plano
                  </Button>
                }
              >
                {billingHint}
              </Alert>
            ) : null}

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
                <Step key={label}>
                <StepLabel>{label}</StepLabel>
                </Step>
            ))}
            </Stepper>

            {renderStepContent()}

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, width: "100%" }}>
              <Button
                color="inherit"
                onClick={() => navigate("/chat")}
              >
                {t("setup.exitToApp")}
              </Button>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button onClick={handleBack} disabled={activeStep === 0}>
                  {t("setup.back")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={activeStep >= steps.length || loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : activeStep === steps.length - 1 ? (
                    t("setup.finish")
                  ) : (
                    t("setup.next")
                  )}
                </Button>
              </Box>
            </Box>
        </Paper>
        <Box mt={4} pb={2} display="flex" justifyContent="center" alignItems="center">
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {t("setup.footer", { year: new Date().getFullYear() })}
            </Typography>
        </Box>
    </Box>
  );
};

export default SetupPage;
